import React, { useState, useEffect } from 'react'
import { getTasks } from '../utils/supabase'
import TaskList from './TaskList'
import TaskModal from './TaskModal'
import ShareModal from './ShareModal'
import DeleteModal from './DeleteModal'

const TaskManager = () => {
  const [tasks, setTasks] = useState([])
  const [filteredTasks, setFilteredTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCompleted, setShowCompleted] = useState(false)
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [sharingTask, setSharingTask] = useState(null)
  const [deletingTask, setDeletingTask] = useState(null)

  const loadTasks = async () => {
    try {
      setLoading(true)
      const { data, error } = await getTasks()
      if (error) throw error
      setTasks(data || [])
    } catch (error) {
      setError('Failed to load tasks: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTasks()
  }, [])

  useEffect(() => {
    // Filter tasks based on search and completed visibility
    let filtered = tasks.filter(task => {
      const matchesSearch = 
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.creator_email?.toLowerCase().includes(searchTerm.toLowerCase())

      // Hide only completed tasks when showCompleted is false
      const shouldHide = task.completed && !showCompleted
      const includeTask = !shouldHide

      return matchesSearch && includeTask
    })

    // Sort by created date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    setFilteredTasks(filtered)
  }, [tasks, searchTerm, showCompleted])

  const handleCreateTask = () => {
    setEditingTask(null)
    setShowTaskModal(true)
  }

  const handleEditTask = (task) => {
    setEditingTask(task)
    setShowTaskModal(true)
  }

  const handleShareTask = (task) => {
    setSharingTask(task)
    setShowShareModal(true)
  }

  const handleDeleteTask = (task) => {
    setDeletingTask(task)
    setShowDeleteModal(true)
  }

  const handleTaskSaved = (savedTask) => {
    if (editingTask) {
      // Update existing task optimistically
      setTasks(prev => prev.map(task => 
        task.id === savedTask.id ? savedTask : task
      ))
    } else {
      // Add new task optimistically
      setTasks(prev => [savedTask, ...prev])
    }
    setShowTaskModal(false)
    setEditingTask(null)
  }

  const handleTaskDeleted = (taskId) => {
    // Remove task optimistically
    setTasks(prev => prev.filter(task => task.id !== taskId))
    setShowDeleteModal(false)
    setDeletingTask(null)
  }

  const handleTaskShared = (updatedTask) => {
    // Update task with new sharing info optimistically
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ))
    setShowShareModal(false)
    setSharingTask(null)
  }

  const handleTaskUpdate = (updatedTask) => {
    // Handle task completion toggle and subtask updates optimistically
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ))
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading tasks...</p>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="controls">
        <div className="controls-row">
          <input
            type="text"
            className="search-box"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleCreateTask}>
            + New Task
          </button>
          <button
            className={`btn ${showCompleted ? 'btn-secondary' : 'btn-success'}`}
            onClick={() => setShowCompleted(!showCompleted)}
          >
            {showCompleted ? 'Hide Completed' : 'Show Completed'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <TaskList
        tasks={filteredTasks}
        onEdit={handleEditTask}
        onShare={handleShareTask}
        onDelete={handleDeleteTask}
        onTaskUpdate={handleTaskUpdate}
      />

      {filteredTasks.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No tasks found</h3>
          <p>Try adjusting your search or filters, or create a new task.</p>
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          task={editingTask}
          onSave={handleTaskSaved}
          onClose={() => setShowTaskModal(false)}
        />
      )}

      {showShareModal && sharingTask && (
        <ShareModal
          task={sharingTask}
          onShare={handleTaskShared}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showDeleteModal && deletingTask && (
        <DeleteModal
          task={deletingTask}
          onDelete={handleTaskDeleted}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  )
}

export default TaskManager